import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiDelete } from '../api/http';
import './global.css';

export default function LayoutsList() {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  async function loadLayouts() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet('/api/layouts');
      if (Array.isArray(data)) {
        setLayouts(data);
      } else {
        setError('Unexpected response from server.');
        setLayouts([]);
      }
    } catch (err) {
      setError(err.message || 'Unable to fetch layouts. Please try again later.');
      setLayouts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLayouts();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this layout?')) return;
    try {
      await apiDelete(`/api/layouts/${id}`);
      loadLayouts();
    } catch (err) {
      alert('Failed to delete layout: ' + err.message);
    }
  };

  const handleCopy = async (id) => {
    try {
      const layout = await apiGet(`/api/layouts/${id}`);
      const newLayout = {
        name: layout.name + ' Copy',
        version: layout.version + 1,
        json: JSON.parse(layout.json),
        is_active: layout.is_active,
      };
      await apiPost('/api/layouts', newLayout);
      loadLayouts();
      alert('Layout copied successfully');
    } catch (err) {
      alert('Failed to copy layout: ' + err.message);
    }
  };

  const handleImport = (ev) => {
    const file = ev.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const json = JSON.parse(e.target.result);
        if (!Array.isArray(json)) {
          throw new Error('Invalid layout format (expected array)');
        }

        const newLayout = {
          name: file.name.replace('.json', '') + ' Imported',
          version: 1,
          json,
          is_active: true,
        };

        await apiPost('/api/layouts', newLayout);
        loadLayouts();
        alert('Layout imported successfully');
      } catch (err) {
        alert('Import failed: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="card layouts-list">
      <h2>Layouts</h2>
      <div className="action-buttons" style={{ marginBottom: '1em' }}>
        <button className="btn primary" onClick={() => navigate('/layout-editor/new')}>
          + New Layout
        </button>
        <label className="btn secondary" style={{ cursor: 'pointer', marginLeft: '0.5em' }}>
          Import Layout
          <input
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImport}
          />
        </label>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : layouts.length === 0 ? (
        <p className="empty-state">No layouts found.</p>
      ) : (
        <table className="styled-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Version</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {layouts.map((l) => (
              <tr key={l.id}>
                <td data-label="ID">{l.id}</td>
                <td data-label="Name">{l.name}</td>
                <td data-label="Version">{l.version}</td>
                <td data-label="Active">{l.is_active ? 'Yes' : 'No'}</td>
                <td data-label="Actions">
                  <button
                    className="btn secondary small"
                    onClick={() => navigate(`/layout-editor/edit/${l.id}`)}
                  >
                    Edit
                  </button>{' '}
                  <button className="btn secondary small" onClick={() => handleCopy(l.id)}>
                    Copy
                  </button>{' '}
                  <button className="btn danger small" onClick={() => handleDelete(l.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
